/**
 * Dónde la serie de check-ins tiene huecos, y cómo cortarla para que se vean.
 *
 * EL PROBLEMA QUE RESUELVE
 * Con el eje temporal, dos check-ins separados por tres semanas quedan lejos en
 * la pantalla — pero la línea los sigue uniendo con una pendiente continua, y
 * eso se lee como una transición gradual. De ese período no hay ningún dato: la
 * persona simplemente no contestó. Dibujar una pendiente ahí es afirmar algo que
 * nadie midió, y encima con el eje temporal ocupa más pantalla que antes, así que
 * la afirmación falsa se ve más.
 *
 * EL CRITERIO: DOS PERÍODOS DEL PROPIO PACIENTE
 * Un hueco no son "muchos días", son días RELATIVOS a cada cuánto se espera que
 * conteste. Tres semanas sin datos es un abandono para alguien con seguimiento
 * semanal y es lo normal para alguien mensual; un umbral fijo en días cortaría
 * todas las series mensuales y ninguna semanal.
 *
 * Se corta a partir de DOS períodos porque ahí la lectura es exacta y se puede
 * decir en una frase: entre estos dos puntos falta al menos un check-in que se
 * esperaba. Una separación de un período es simplemente el siguiente check-in,
 * aunque llegue con un par de días de atraso — cortar ahí llenaría de huecos una
 * serie perfectamente normal.
 *
 * Y es el mismo multiplicador que `MULTIPLICADOR_RIESGO` en
 * backend/src/estado.js. No es casualidad: son la misma pregunta —"¿pasó
 * demasiado tiempo sin datos?"— y conviene que la línea se corte exactamente
 * cuando el semáforo se puso rojo. Con dos umbrales distintos, la lista diría "en
 * riesgo" mientras el gráfico dibuja continuidad.
 *
 * POR QUÉ ESTE ARCHIVO ES APARTE Y TIENE PRUEBAS
 * Es una regla que se equivoca en silencio: si el umbral queda muy sensible, la
 * serie aparece picada y nadie sabe por qué; si queda muy laxo, vuelve la
 * pendiente falsa. Ninguna de las dos rompe nada, así que sin pruebas se
 * descubren mirando.
 */

export const DIA_EN_MS = 24 * 60 * 60 * 1000;

/** Cuántos períodos de separación hacen falta para hablar de hueco. */
export const PERIODOS_PARA_HUECO = 2;

/** Lo que se asume si no hay frecuencia ni datos para inferirla. */
export const FRECUENCIA_POR_DEFECTO = 7;

/**
 * La separación típica de una serie, en días.
 *
 * MEDIANA Y NO PROMEDIO. Un solo hueco enorme arrastra el promedio hacia arriba
 * y con eso el umbral se vuelve tan grande que ese mismo hueco deja de contar —
 * o sea que el promedio esconde justo el caso que hay que detectar. La mediana no
 * se mueve por un valor extremo.
 */
export function separacionTipica(instantes) {
  const orden = (Array.isArray(instantes) ? instantes : [])
    .filter((t) => Number.isFinite(t))
    .slice()
    .sort((a, b) => a - b);
  if (orden.length < 2) return null;

  const seps = [];
  for (let i = 1; i < orden.length; i++) seps.push((orden[i] - orden[i - 1]) / DIA_EN_MS);
  seps.sort((a, b) => a - b);
  const medio = Math.floor(seps.length / 2);
  return seps.length % 2 ? seps[medio] : (seps[medio - 1] + seps[medio]) / 2;
}

/**
 * Los tramos sin datos de la serie.
 *
 * Devuelve `[{ desde, hasta, dias }]` con los instantes de los dos check-ins que
 * bordean el hueco. `frecuenciaDias` es la del paciente; si no viene, se infiere
 * de la propia serie.
 */
export function huecosDeLaSerie(instantes, frecuenciaDias) {
  const orden = (Array.isArray(instantes) ? instantes : [])
    .filter((t) => Number.isFinite(t))
    .slice()
    .sort((a, b) => a - b);
  if (orden.length < 2) return [];

  const declarada = Number(frecuenciaDias);
  const frecuencia = Number.isFinite(declarada) && declarada > 0
    ? declarada
    : (separacionTipica(orden) || FRECUENCIA_POR_DEFECTO);

  const umbral = PERIODOS_PARA_HUECO * frecuencia * DIA_EN_MS;
  const huecos = [];
  for (let i = 1; i < orden.length; i++) {
    const salto = orden[i] - orden[i - 1];
    if (salto >= umbral) {
      huecos.push({
        desde: orden[i - 1],
        hasta: orden[i],
        dias: Math.round(salto / DIA_EN_MS),
      });
    }
  }
  return huecos;
}

/**
 * Mete una fila vacía en el medio de cada hueco, para que la línea se corte.
 *
 * Con `connectNulls={false}`, Recharts deja de dibujar al encontrar un punto sin
 * valor. La fila va en el MEDIO del hueco y no pegada a uno de los bordes para
 * que el corte se lea como "acá no hay nada" y no como "este punto falló".
 *
 * `claves` son los pilares que hay que poner en null: si se dejara alguno afuera,
 * esa serie seguiría unida y el gráfico mostraría unas líneas cortadas y otras no
 * sobre el mismo período, que es más confuso que no cortar ninguna.
 */
export function conCortes(filas, huecos, claves) {
  if (!Array.isArray(huecos) || huecos.length === 0) return filas;

  const vacia = (t) => {
    const fila = { t, __corte: true };
    (claves || []).forEach((k) => { fila[k] = null; });
    return fila;
  };

  const salida = [...filas];
  huecos.forEach((h) => salida.push(vacia(h.desde + (h.hasta - h.desde) / 2)));
  return salida.sort((a, b) => a.t - b.t);
}
